import { addDays, getISODay } from 'date-fns';
import toDate from 'date-fns/toDate';
import moment from 'moment-mini';

export const realNumber = (value) =>
  value && !/^\d+(\.\d{1,2})?$/i.test(value) ? 'Invalid number' : undefined;

// ✅ Non-mutating + handles non-plain values safely
export const objectToArray = (object) => {
  if (!object) return [];
  return Object.entries(object).map(([id, value]) => {
    const isPlainObject =
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.getPrototypeOf(value) === Object.prototype;

    return isPlainObject ? { id, ...value } : { id, value };
  });
};
export const portalVersion = process.env.REACT_APP_VERSION || '0.0.0';
export const versionDate = portalVersion;

export const createNewSession = (user, photoURL, session) => {
  return {
    ...session,
    billed: 'Unbilled',
    completed: false,
    confirmedTimes: false,
    confirmedServices: false,
    creatorUid: user.uid,
    createdBy: user.displayName,
    creatorPhotoURL: photoURL || '/assets/user.png',
    created: Date.now(),
    portalVersion: versionDate,
    telehealth: false,
    timecardAdded: false,
    hoursAndMins: 0,
    attendees: {
      [user.uid]: {
        going: true,
        joinDate: Date.now(),
        photoURL: photoURL || '/assets/user.png',
        displayName: user.displayName,
        creator: true,
      },
    },
  };
};

export const createNewSupervision = (user, photoURL, supervision) => {
  return {
    ...supervision,
    creatorUid: user.uid,
    createdBy: user.displayName,
    creatorPhotoURL: photoURL || '/assets/user.png',
    created: Date.now(),
    portalVersion: versionDate,
    completed: false,
    timecardAdded: false,
    attendees: {
      [user.uid]: {
        going: true,
        joinDate: Date.now(),
        photoURL: photoURL || '/assets/user.png',
        displayName: user.displayName,
        creator: true,
      },
    },
  };
};

export const createNewPlan = (user, photoURL, plan) => {
  plan.date = toDate(plan.date);
  plan.dateEnd = toDate(plan.dateEnd);
  return {
    ...plan,
    creatorUid: user.uid,
    createdBy: user.displayName,
    creatorPhotoURL: photoURL || '/assets/user.png',
    created: Date.now(),
    portalVersion: versionDate,
    completed: false,
    attendees: {
      [user.uid]: {
        going: true,
        joinDate: Date.now(),
        photoURL: photoURL || '/assets/user.png',
        displayName: user.displayName,
        creator: true,
      },
    },
  };
};

export const createNewDocument = (user, photoURL, document) => {
  document.date = document.date ? toDate(document.date) : toDate(Date.now());
  document.dateEnd = document.dateEnd ? toDate(document.dateEnd) : null;
  return {
    ...document,
    creatorUid: user.uid,
    createdBy: user.displayName,
    creatorPhotoURL: photoURL || '/assets/user.png',
    created: Date.now(),
    portalVersion: versionDate,
    completed: false,
    attendees: {
      [user.uid]: {
        going: true,
        joinDate: Date.now(),
        photoURL: photoURL || '/assets/user.png',
        displayName: user.displayName,
        creator: true,
      },
    },
  };
};

export const createNewReview = (user, photoURL, review) => {
  return {
    ...review,
    creatorUid: user.uid,
    createdBy: user.displayName,
    creatorPhotoURL: photoURL || '/assets/user.png',
    created: Date.now(),
    portalVersion: versionDate,
    completed: false,
    attendees: {
      [user.uid]: {
        going: true,
        joinDate: Date.now(),
        photoURL: photoURL || '/assets/user.png',
        displayName: user.displayName,
        creator: true,
      },
    },
  };
};

export const createNewEvent = (user, photoURL, event) => {
  event.date = toDate(event.date);
  return {
    ...event,
    creatorUid: user.uid,
    createdBy: user.displayName,
    creatorPhotoURL: photoURL || '/assets/user.png',
    created: Date.now(),
    portalVersion: versionDate,
    completed: false,
    attendees: {
      [user.uid]: {
        going: true,
        joinDate: Date.now(),
        photoURL: photoURL || '/assets/user.png',
        displayName: user.displayName,

        creator: true,
      },
    },
  };
};

export const createNewNondirect = (user, photoURL, nondirect) => {
  return {
    ...nondirect,
    creatorUid: user.uid,
    createdBy: user.displayName,
    creatorPhotoURL: photoURL || '/assets/user.png',
    created: Date.now(),
    portalVersion: versionDate,
    completed: false,
    attendees: {
      [user.uid]: {
        going: true,
        joinDate: Date.now(),
        photoURL: photoURL || '/assets/user.png',
        displayName: user.displayName,
        creator: true,
      },
    },
  };
};

export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = (R * c * 0.621371).toFixed(2);
  return distance; // in miles
};

export const getClosestDayOfLastWeek = (
  dayOfWeek,
  fromDate = new Date(new Date().toDateString()),
) => {
  const dayOfWeekMap = {
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thur: 4,
    Fri: 5,
    Sat: 6,
    Sun: 7,
  };
  const offsetDays = -(getISODay(fromDate) - dayOfWeekMap[dayOfWeek]);
  return addDays(fromDate, offsetDays);
};

export const createDataTree = (dataset) => {
  let hashTable = Object.create(null);
  dataset.forEach((a) => (hashTable[a.id] = { ...a, childNodes: [] }));
  let dataTree = [];
  dataset.forEach((a) => {
    if (a.parentId) hashTable[a.parentId].childNodes.push(hashTable[a.id]);
    else dataTree.push(hashTable[a.id]);
  });
  return dataTree;
};

export const formatInputForMoment = (time) => {
  // add today's date to time-only ("HH:mm a") format
  if (/[0-9]{2}:[0-9]{2} [AM|PM]/.test(time)) {
    return moment(moment().format('YYYYMMDD') + ' ' + time, 'YYYYMMDD HH:mm a');
  }
  // add other format conversions here
  // otherwise, 'time' expected to be valid for creating object
  return moment(time);
};

export const formatTimeInQuarterHours = (start, end) => {
  const mStart = formatInputForMoment(start);
  const mEnd = formatInputForMoment(end);

  const mTotalMinutes = mEnd.diff(mStart, 'minutes');
  const hours = Math.floor(mTotalMinutes / 60);
  const mins = mTotalMinutes - hours * 60;
  const quarters = Math.round(mins / 15) / 4;
  return hours + quarters;
};

export const formatTimeForRoundingRule = (start, end) => {
  const mStart = formatInputForMoment(start);
  const mEnd = formatInputForMoment(end);

  const mTotalMinutes = mEnd.diff(mStart, 'minutes');
  const unitsExact = mTotalMinutes * 0.06666667;

  let unitNumber = parseFloat(unitsExact?.toString()?.split('.')[0]);
  let unitDecimal = unitsExact?.toString()?.split('.')[1];
  let unitHundredth = parseFloat(unitDecimal[2]);
  unitDecimal = parseFloat('.' + unitDecimal);

  if (unitDecimal >= 0.8) {
    unitDecimal = 1;
  } else if (unitHundredth === 0 || unitHundredth >= 5) {
    unitDecimal = (Math.round(unitDecimal * 100) / 100)
      .toFixed(1)
      .replace(/^0+/, '');
  } else {
    unitDecimal = (Math.round(unitDecimal * 100) / 100)
      .toFixed(2)
      .replace(/^0+/, '');
  }
  let totalUnits = unitNumber + unitDecimal;

  return totalUnits;
};
